// Frontend RUM + session replay (ADR 0086). Two client-observability integrations,
// BOTH env-gated and lazy-loaded so a build with neither configured pays nothing:
//
//   • Grafana Faro (web SDK + tracing) — browser errors, web-vitals, logs, and
//     traces, shipped to the LGTM stack (ADR 0069) via a Grafana Alloy faro
//     receiver. This is what catches the client-side issues our server telemetry
//     can't see (a render crash, a failed fetch, a slow interaction).
//   • OpenReplay tracker — session replay, to see exactly what a tester did.
//
// Config comes from `window.__RUM__`, set by the served `/rum.js` (see
// web/public/rum.js) so it's per-environment WITHOUT a rebuild: dev/tests get the
// empty default (both off); beta's deploy writes the real endpoints. Both SDKs are
// dynamic-imported INSIDE init, so they're code-split out of the main bundle and
// only fetched when actually configured (the ADR 0071 lazy-load pattern).

export interface RumConfig {
  /** Grafana Alloy faro.receiver collect URL (same-origin `/faro` on beta). */
  faroUrl?: string;
  /** Faro app name (defaults to "legends-web"). */
  faroApp?: string;
  /** OpenReplay project key — enables session replay when set. */
  openReplayProjectKey?: string;
  /** OpenReplay ingest endpoint (self-hosted; omit for OpenReplay Cloud default). */
  openReplayIngest?: string;
  /** Deployment environment tag (e.g. "beta") stamped on all RUM data. */
  env?: string;
  /** App version stamped on Faro events. */
  version?: string;
  /**
   * Google Analytics 4 measurement id (e.g. "G-XXXXXXXXXX") — enables GA4 when
   * set, a no-op when unset (ADR 0224). Served from the same `/rum.js` runtime
   * config as the RUM fields, so it's per-environment WITHOUT a rebuild; the GA
   * module (see web/src/ga.ts) reads it via {@link rumConfig}.
   */
  ga4Id?: string;
}

declare global {
  interface Window {
    __RUM__?: RumConfig;
    __RUM_STARTED__?: boolean;
  }
}

/** Read the runtime RUM config injected by `/rum.js` (empty when unconfigured). */
export function rumConfig(): RumConfig {
  if (typeof window === "undefined") return {};
  return window.__RUM__ ?? {};
}

/**
 * Regex signatures for known-benign LiveKit CLIENT-LIBRARY console noise (ADR 0114).
 *
 * LiveKit's SDK emits `console.error` during NORMAL room teardown / reconnect
 * (Exit, End, navigation, reconnect races). Faro's default console
 * instrumentation captures `console.error` as exceptions, so these flood the
 * Grafana exception view and MASK real errors. They are not application bugs and
 * we can't stop LiveKit's internal logging — so we drop them at the Faro layer.
 *
 * Matching is deliberately by substring/shape, NOT the exact channel token, so
 * all three data-channel variants (DATA_TRACK_LOSSY / RELIABLE / LOSSY) and any
 * future channel name are covered. The lone real `NotAllowedError` (camera/mic
 * permission denial) is intentionally NOT listed — it is a genuine signal.
 */
export const IGNORABLE_RUM_ERROR_PATTERNS: RegExp[] = [
  // "publisher data channel 'DATA_TRACK_LOSSY' (/'RELIABLE'/'LOSSY') closed unexpectedly"
  /data channel.*closed unexpectedly/i,
  /PC manager is closed/i,
  /Received leave request while trying to \(re\)connect/i,
  /error reading from signal stream/i,
  // LiveKit SDK token-rejection noise on a stale/expired join attempt — it logs the
  // token VERBATIM ("invalid token: eyJ…"), so dropping it both removes the teardown
  // noise AND stops a LiveKit JWT leaking into telemetry (LSC-597 monitoring review).
  // The `eyJ` anchor scopes it to the JWT-leaking variant (a base64url JWT header).
  /invalid token: eyJ/i,
  // "room connection has timed out (signal)" — a normal reconnect-window timeout.
  /connection has timed out/i,
];

/**
 * True when a browser-exception / console message is known-benign LiveKit
 * teardown/reconnect noise (see {@link IGNORABLE_RUM_ERROR_PATTERNS}). Pure and
 * exported so it's unit-testable without Faro or the DOM; wired into the Faro
 * `beforeSend` drop hook below. Genuine errors return false and keep flowing to
 * Grafana — we never disable error capture wholesale (ADR 0114).
 */
export function isIgnorableRumError(message: string): boolean {
  if (!message) return false;
  return IGNORABLE_RUM_ERROR_PATTERNS.some((re) => re.test(message));
}

/**
 * Initialize whatever RUM integrations are configured. Idempotent (guards a
 * double-init) and best-effort — a failure in an observability SDK must NEVER
 * break the app, so every path is wrapped and swallowed. Returns immediately;
 * the SDKs load asynchronously via dynamic import.
 */
export function initRum(cfg: RumConfig = rumConfig()): void {
  if (typeof window === "undefined") return;
  if (window.__RUM_STARTED__) return;
  const wantFaro = !!cfg.faroUrl;
  const wantReplay = !!cfg.openReplayProjectKey;
  if (!wantFaro && !wantReplay) return; // nothing configured — pay nothing
  window.__RUM_STARTED__ = true;

  if (wantFaro) void startFaro(cfg);
  if (wantReplay) void startOpenReplay(cfg);
}

async function startFaro(cfg: RumConfig): Promise<void> {
  try {
    const [{ initializeFaro, getWebInstrumentations }, { TracingInstrumentation }] = await Promise.all([
      import("@grafana/faro-web-sdk"),
      import("@grafana/faro-web-tracing"),
    ]);
    initializeFaro({
      url: cfg.faroUrl!,
      app: {
        name: cfg.faroApp || "legends-web",
        version: cfg.version || "dev",
        environment: cfg.env || "dev",
      },
      instrumentations: [...getWebInstrumentations(), new TracingInstrumentation()],
      // Drop the benign LiveKit teardown/reconnect console noise (ADR 0114) so it
      // doesn't mask real errors. `ignoreErrors` covers exceptions pushed with a
      // message/name/stack; the `beforeSend` hook backstops it and also catches the
      // console-as-log path — genuine errors are left untouched.
      ignoreErrors: IGNORABLE_RUM_ERROR_PATTERNS,
      beforeSend: (item) => {
        try {
          const payload = item?.payload as { value?: unknown; message?: unknown } | undefined;
          const text =
            typeof payload?.value === "string"
              ? payload.value
              : typeof payload?.message === "string"
                ? payload.message
                : "";
          if (text && isIgnorableRumError(text)) return null;
        } catch {
          // A filter hook must never throw — keep the item on any surprise.
        }
        return item;
      },
    });
  } catch {
    // Never let RUM setup break the app.
  }
}

async function startOpenReplay(cfg: RumConfig): Promise<void> {
  try {
    const mod = await import("@openreplay/tracker");
    const Tracker = mod.default;
    const tracker = new Tracker({
      projectKey: cfg.openReplayProjectKey!,
      ...(cfg.openReplayIngest ? { ingestPoint: cfg.openReplayIngest } : {}),
    });
    void tracker.start();
  } catch {
    // Session replay is best-effort — a failure must not affect the app.
  }
}
